import React, { useEffect, useState, useCallback } from "react";
import { L as Link } from "../lib/i18n";
import { api } from "../lib/api";
import { useActiveLocale } from "../lib/i18n";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Search, ShieldCheck, MapPin, Calendar, Building2, Loader2 } from "lucide-react";
import { useSeoHead } from "../lib/seoHead";
import { getPageSeo, buildHreflangs, buildBreadcrumbList, buildCollectionPageSchema } from "../lib/pageSeo";
import JsonLd from "../components/JsonLd";

const SITE_URL = (process.env.REACT_APP_PUBLIC_URL || "").replace(/\/$/, "");

export default function Dealers() {
  const locale = useActiveLocale();
  const [data, setData] = useState({ items: [], total: 0, page: 1, limit: 24 });
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  // Phase 63 — SEO
  const seo = getPageSeo("dealers", locale);
  useSeoHead({
    title: seo.title,
    description: seo.description,
    canonical: `${SITE_URL}/${locale}/dealers`,
    image: `${SITE_URL}/icons/og-default.png`,
    hreflangs: buildHreflangs(SITE_URL, "/dealers"),
  });
  const breadcrumbs = buildBreadcrumbList([
    { name: "CarHive", url: `${SITE_URL}/${locale}` },
    { name: seo.title.split(" |")[0], url: `${SITE_URL}/${locale}/dealers` },
  ]);
  const collection = buildCollectionPageSchema({
    url: `${SITE_URL}/${locale}/dealers`,
    name: seo.title,
    description: seo.description,
    numberOfItems: data.total || 0,
  });

  const load = useCallback(async (page = 1, query = "") => {
    setLoading(true);
    try {
      setData(await api.listDealers({ q: query || undefined, page, limit: 24 }));
    } catch (err) {
      console.error("dealers list error", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(1, ""); }, [load]);

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8" data-testid="dealers-page">
      {breadcrumbs && <JsonLd id="dealers-breadcrumbs" data={breadcrumbs} />}
      {collection && <JsonLd id="dealers-collection" data={collection} />}
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-600/15 text-red-500">
          <Building2 className="h-5 w-5" />
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-red-500">CarHive · Händler</p>
          <h1 className="font-display text-3xl font-bold text-white sm:text-4xl">Verifizierte Schweizer Händler</h1>
          <p className="mt-2 max-w-xl text-sm text-zinc-400">
            Nur professionelle Händler mit verifiziertem Profil — geprüft vom CarHive-Team.
          </p>
        </div>
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); load(1, q); }}
        className="mt-8 flex gap-2"
      >
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Suche nach Firma, Kanton oder Beschreibung…"
            className="border-zinc-800 bg-black pl-9 text-white"
            data-testid="dealers-search"
          />
        </div>
        <Button type="submit" className="bg-red-600 hover:bg-red-500">Suchen</Button>
      </form>

      <div className="mt-8">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-zinc-500">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : data.items.length === 0 ? (
          <Card className="border-zinc-800 bg-zinc-950 p-12 text-center">
            <Building2 className="mx-auto h-10 w-10 text-zinc-700" />
            <p className="mt-3 font-display text-lg font-bold text-white">Noch keine verifizierten Händler</p>
            <p className="mt-1 text-sm text-zinc-500">Schaue bald wieder vorbei.</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {data.items.map((d) => (
              <Link
                key={d.id}
                to={`/dealers/${d.slug}`}
                className="group block overflow-hidden rounded-xl border border-zinc-800 bg-zinc-950 p-5 transition-all hover:border-red-500/40 hover:bg-zinc-900"
                data-testid={`dealer-card-${d.slug}`}
              >
                <div className="flex items-start gap-3">
                  {d.logo_url ? (
                    <img src={d.logo_url} alt={d.company_name} className="h-12 w-12 rounded-lg object-cover" />
                  ) : (
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-red-600/15 text-red-500">
                      <Building2 className="h-6 w-6" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <h3 className="truncate font-display text-base font-semibold text-white group-hover:text-red-300">{d.company_name}</h3>
                      <ShieldCheck className="h-4 w-4 shrink-0 text-red-500" title="Verifiziert" />
                    </div>
                    {d.canton && (
                      <p className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-zinc-500">
                        <MapPin className="h-3 w-3" /> {d.canton}
                        {d.since_year && <><span className="mx-1">·</span><Calendar className="h-3 w-3" /> seit {d.since_year}</>}
                      </p>
                    )}
                  </div>
                </div>
                {d.description && (
                  <p className="mt-3 line-clamp-2 text-xs leading-relaxed text-zinc-400">{d.description}</p>
                )}
                <div className="mt-4 flex items-center justify-between border-t border-zinc-800 pt-3 text-xs">
                  <span className="text-zinc-500">{d.active_listings_count || 0} aktive Inserate</span>
                  <span className="text-red-400 group-hover:translate-x-0.5 transition-transform">→</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
